import { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { IconButton, Tooltip } from '@folio/stripes/components';

import css from './CopyButton.css';

const propTypes = {
  userMessage: PropTypes.string.isRequired,
  botMessage: PropTypes.string.isRequired,
  sources: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  sources: [],
};

const CopyButton = ({ userMessage, botMessage, sources }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const formatCopyText = () => {
    // If no bot message, just copy the user message (question only)
    if (!botMessage || botMessage.trim() === '') {
      return userMessage;
    }
    
    // If we have both question and answer
    let text = `${intl.formatMessage({ id: 'ui-plugin-smart-help.copy.question' })}: ${userMessage}\n\n`;
    text += `${intl.formatMessage({ id: 'ui-plugin-smart-help.copy.answer' })}: ${botMessage}`;
    
    if (sources && sources.length > 0) {
      text += `\n\n${intl.formatMessage({ id: 'ui-plugin-smart-help.copy.sources' })}:\n`;
      sources.forEach((source, index) => {
        text += `${index + 1}. ${source}\n`;
      });
    }
    
    return text;
  };

  const handleCopy = async () => {
    try {
      const textToCopy = formatCopyText();
      
      if (navigator.clipboard && window.isSecureContext) {
        // Modern Clipboard API
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const tooltipText = copied
    ? intl.formatMessage({ id: 'ui-plugin-smart-help.copy.copied' })
    : (!botMessage || botMessage.trim() === '') 
      ? intl.formatMessage({ id: 'ui-plugin-smart-help.copy.question-only' })
      : intl.formatMessage({ id: 'ui-plugin-smart-help.copy.tooltip' });

  return (
    <Tooltip 
      id={`copy-tooltip-${Date.now()}`}
      text={tooltipText}
      placement="top"
    >
      {({ ref, ariaIds }) => (
        <IconButton
          ref={ref}
          icon={copied ? "check" : "duplicate"}
          onClick={handleCopy}
          className={`${css.copyButton} ${copied ? css.copied : ''}`}
          {...ariaIds}
        />
      )}
    </Tooltip>
  );
};

CopyButton.propTypes = propTypes;
CopyButton.defaultProps = defaultProps;

export default CopyButton;